import { useEffect, useState } from 'react';
import { useFragment } from '@frameio/graphql-client';
import { ProjectWorkspaceInfo } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';

/**
 * Manages the selected project for an operation, defaulting to the
 * active project from context but allowing for temporary overrides.
 *
 * @returns The selected project's information and a function to change the selection.
 */
export const useProjectLocationSelector = () => {
  const [projectId, setProjectId] = useState<string | null>(null);
  const activeProjectId = useActiveProjectId();

  useEffect(() => {
    if (activeProjectId && !projectId) {
      setProjectId(activeProjectId);
    }
  }, [activeProjectId, projectId]);

  const onProjectIdChange = (maybeProjectId: string | null) => {
    if (maybeProjectId === null && !!activeProjectId) {
      setProjectId(activeProjectId);
      return;
    }

    setProjectId(maybeProjectId);
  };

  const { data } = useFragment({
    fragment: ProjectWorkspaceInfo,
    fragmentName: 'ProjectWorkspaceInfo',
    from: {
      __typename: 'Project',
      id: projectId,
    },
  });

  const project = data?.id ? data : null;

  return {
    projectId: project?.id ?? null,
    projectRootAssetId: project?.rootAssetId ?? null,
    projectName: project?.name ?? null,
    onProjectIdChange,
  };
};
